import React from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon } from 'lucide-react';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-50px'
  },
  transition: {
    duration: 0.6
  }
};
function DiaryDate({
  date
}: {
  date: string;
}) {
  return <div className="flex items-center gap-2 mb-4" data-id="element-57">
      <CalendarIcon className="w-4 h-4" style={{
      color: 'var(--color-brown)'
    }} data-id="element-58" />
      <span className="font-handwritten text-xl" style={{
      color: 'var(--color-brown)'
    }} data-id="element-59">
        {date}
      </span>
    </div>;
}
export function StorySection() {
  return <section className="w-full" data-id="element-60">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-12" data-id="element-61">
        {/* Before */}
        <motion.div {...fadeInUp} className="rounded-xl p-6 md:p-8" style={{
        backgroundColor: 'var(--color-cream-dark)'
      }} data-id="element-62">
          <DiaryDate date="14. März 2024" data-id="element-63" />
          <div className="space-y-4 text-base md:text-lg leading-relaxed" style={{
          color: 'var(--color-text)'
        }} data-id="element-64">
            <p data-id="element-65">
              Ich weiß nicht mehr weiter. Luna hat seit Wochen wieder Durchfall.
              Gestern Nacht musste ich dreimal mit ihr raus – um 2 Uhr, um 4 Uhr
              und nochmal um 6. Sie schaut mich mit diesen großen Augen an, als
              würde sie sich entschuldigen wollen. Dabei kann sie doch gar
              nichts dafür.
            </p>
            <p data-id="element-66">
              Wir waren schon bei <strong data-id="element-67">drei verschiedenen Tierärzten</strong>
              . Ich habe bestimmt zehn verschiedene Futtersorten ausprobiert.
              Hypoallergen, getreidefrei, mit Reis, ohne Reis... Nichts hat
              wirklich geholfen. Jedes Mal dachte ich:{' '}
              <em data-id="element-68">Jetzt wird es besser.</em> Und dann ging es wieder von vorne
              los.
            </p>
            <p data-id="element-69">
              Das Schlimmste ist, zu sehen, wie sie leidet. Sie frisst kaum
              noch, hat abgenommen und spielt nicht mehr so wie früher. Mein
              fröhlicher, wilder Wirbelwind ist nur noch ein Schatten seiner
              selbst.
            </p>
          </div>
        </motion.div>

        {/* Discovery */}
        <motion.div {...fadeInUp} className="text-center py-6" data-id="element-70">
          <div className="inline-block px-6 py-3 rounded-full" style={{
          backgroundColor: 'var(--color-green-light)'
        }} data-id="element-71">
            <p className="font-handwritten text-2xl md:text-3xl" style={{
            color: 'var(--color-green)'
          }} data-id="element-72">
              ✦ Der Wendepunkt ✦
            </p>
          </div>
        </motion.div>

        <motion.div {...fadeInUp} className="rounded-xl p-6 md:p-8 border-l-4" style={{
        backgroundColor: 'white',
        borderColor: 'var(--color-green)'
      }} data-id="element-73">
          <DiaryDate date="2. April 2024" data-id="element-74" />
          <div className="space-y-4 text-base md:text-lg leading-relaxed" style={{
          color: 'var(--color-text)'
        }} data-id="element-75">
            <p data-id="element-76">
              Eine Freundin aus der Hundegruppe hat mir letzte Woche von einem{' '}
              <strong data-id="element-77">natürlichen Futtertopping</strong> erzählt. Ihr Labrador
              hatte ähnliche Probleme, und seit sie das Topping übers Futter
              streut, ist er wie ausgewechselt.
            </p>
            <p data-id="element-78">
              Ich war erst skeptisch – wie oft hatte ich schon etwas
              „Wunderbares" ausprobiert? Aber dann habe ich mir die
              Inhaltsstoffe angeschaut:{' '}
              <strong data-id="element-79">
                Präbiotika, natürliche Kräuter, keine künstlichen Zusätze
              </strong>
              . Das klang anders als alles, was ich bisher versucht hatte. Kein
              leeres Versprechen, sondern durchdachte Zutaten.
            </p>
            <p className="italic" style={{
            color: 'var(--color-text-light)'
          }} data-id="element-80">
              Ich dachte mir: Was habe ich noch zu verlieren? Also habe ich es
              bestellt.
            </p>
          </div>
        </motion.div>

        <motion.div {...fadeInUp} className="flex justify-center" data-id="element-81">
          <div className="rounded-2xl overflow-hidden shadow-md w-full" data-id="element-82">
            <img src="https://cdn.magicpatterns.com/uploads/umXHXkRpZ6o5HCKnNs98rv/DSC09939-2.jpg" alt="Hund mit Hunderunde Futtertopping Magen & Darm" className="w-full object-contain" data-id="element-83" />
          </div>
        </motion.div>

        {/* After */}
        <motion.div {...fadeInUp} className="rounded-xl p-6 md:p-8" style={{
        backgroundColor: 'var(--color-green-light)'
      }} data-id="element-84">
          <DiaryDate date="28. April 2024" data-id="element-85" />
          <div className="space-y-4 text-base md:text-lg leading-relaxed" style={{
          color: 'var(--color-text)'
        }} data-id="element-86">
            <p data-id="element-87">
              Ich kann es kaum glauben, aber ich muss es aufschreiben:{' '}
              <strong data-id="element-88">Luna geht es so gut wie seit Monaten nicht mehr.</strong>
            </p>
            <p data-id="element-89">
              Schon nach der ersten Woche wurde der Stuhlgang fester. Nach zwei
              Wochen – keine nächtlichen Notfälle mehr. Und jetzt, nach fast
              vier Wochen? Luna frisst wieder mit Begeisterung, sie tobt durch
              den Garten und hat sogar wieder zugenommen.
            </p>
            <p data-id="element-90">
              Ich streue einfach morgens einen Löffel über ihr Futter – das
              war's. Keine komplizierten Diätpläne, keine teuren Spezialfutter.
              Einfach dieses eine Topping.
            </p>
            <p className="font-semibold text-lg md:text-xl" style={{
            color: 'var(--color-green-dark)'
          }} data-id="element-91">
              Ich habe meine Luna zurück. ♥
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}